import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import { getAdminRolePermissionAPI, loginAPI, logoutAPI } from "./userAPI";

const initialState = {
  userName: "",
  isLoggedIn: false,
  status: "idle",
  roleName: "",
  rolePermissions: [],
  managedRoles: [],
  error: null,
  userId: null,
  token: null,
  refreshToken: null,
};

export const login = createAsyncThunk(
  "user/login",
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      const data = await loginAPI(credentials);

      // Write to localStorage BEFORE returning so that the fulfilled reducer
      // and any subsequent API calls (e.g. getAdminRolePermission) already
      // have the token available via the axios interceptor.
      if (data.data?.accessToken) {
        localStorage.setItem("authToken", data.data.accessToken);
        localStorage.setItem("refreshToken", data.data.refreshToken);
        localStorage.setItem("adminName", data.data.admin.name);
        localStorage.setItem("adminId", data.data.admin._id);
      }

      dispatch(
        showModal({ message: "تم تسجيل الدخول بنجاح", status: "success" }),
      );
      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "خطأ في بيانات المستخدم!", status: "error" }),
      );
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

export const logout = createAsyncThunk(
  "user/logout",
  (_, { dispatch, rejectWithValue }) => {
    try {
      logoutAPI();
      dispatch(
        showModal({ message: "تم تسجيل الخروج بنجاح", status: "success" }),
      );
      return true;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ في تسجيل الخروج", status: "error" }),
      );
      return rejectWithValue("Logout failed");
    }
  },
);

export const getAdminRolePermission = createAsyncThunk(
  "user/getAdminRolePermission",
  async (adminId, { rejectWithValue }) => {
    try {
      const data = await getAdminRolePermissionAPI(adminId);
      return data;
    } catch (error) {
      return rejectWithValue("fetching admin role failed");
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.userName = action.payload.userName;
      state.userId = action.payload.userId;
      state.isLoggedIn = true;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken || null;
    },
    clearUser: (state) => {
      state.userName = "";
      state.isLoggedIn = false;
      state.roleName = "";
    },
    /** Keeps socket + Redux in sync when axios refreshes access/refresh tokens */
    sessionTokensUpdated: (state, action) => {
      if (action.payload.accessToken != null) {
        state.token = action.payload.accessToken;
      }
      if (action.payload.refreshToken != null) {
        state.refreshToken = action.payload.refreshToken;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userName = action.payload.data.admin.name;
        state.userId = action.payload.data.admin._id;
        state.roleName = action.payload.data.admin.role?.role || "";
        state.isLoggedIn = true;
        state.token = action.payload.data.accessToken;
        state.refreshToken = action.payload.data.refreshToken;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = "خطأ في بيانات المستخدم";
        state.isLoggedIn = false;
      })

      .addCase(logout.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = "succeeded";
        state.userName = "";
        state.isLoggedIn = false;
        state.roleName = "";
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(getAdminRolePermission.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAdminRolePermission.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.roleName = action.payload.data.role?.role || "";
        state.rolePermissions = action.payload.data.role.permissions;
        state.isLoggedIn = true;
      })
      .addCase(getAdminRolePermission.rejected, (state, action) => {
        state.status = "failed";
        state.error = "خطأ في بيانات المستخدم";
        state.isLoggedIn = false;
      });
  },
});

export const { setUser, clearUser, sessionTokensUpdated } = userSlice.actions;

export default userSlice.reducer;
